import { ref } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { API } from '@/config'
import { useAppStore } from '@/stores/app'

const api = axios.create({ baseURL: API, timeout: 8000 })

/**
 * Who the signed-in user's company is, from GET /api/tenant. Its feature flags override the build/runtime
 * defaults (src/config.ts) so one frontend serves tenants that switch company or cost centers on or off.
 * A flag the server leaves null changes nothing, and a failed call leaves the defaults in place.
 */
export const useTenantStore = defineStore('tenant', () => {
  const slug = ref<string | null>(null)
  const name = ref<string | null>(null)
  const flags = ref<Record<string, boolean | null>>({})

  async function load(): Promise<void> {
    try {
      const res = await api.get('/tenant')
      const data = res.data?.data ?? res.data
      if (!data || typeof data !== 'object') return
      slug.value = data.slug ?? null
      name.value = data.name ?? null
      flags.value = data.flags ?? {}
      // a stack without tenants has no platform flags: its build/runtime config stands
      if (!slug.value) return
      const app = useAppStore()
      if (typeof flags.value.enable_company === 'boolean') app.options.companyEnabled = flags.value.enable_company
      if (typeof flags.value.enable_cost === 'boolean') app.options.costEnabled = flags.value.enable_cost
    } catch {
      /* keep the defaults */
    }
  }

  function clear() {
    slug.value = null
    name.value = null
    flags.value = {}
  }

  return { slug, name, flags, load, clear }
})
